import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { en } from './locales/en';

export type TranslationKey = keyof typeof en;

interface I18nValue {
  /** Look up a key, substituting {name} placeholders. */
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

/**
 * Copy for the whole app. English, left to right.
 *
 * Persian was removed along with the direction handling. Mirroring this app
 * was never a clean win: the charts, the RSI dials, the gap levels and the
 * time axis all run zero-on-the-left by universal convention, so each one had
 * to be pinned back to LTR inside a mirrored page — and the pins ended up
 * outnumbering the prose that benefited.
 *
 * The provider is kept rather than exporting a bare `t`, so none of the call
 * sites change and a second language stays a contained change if it is ever
 * wanted again.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const value = useMemo<I18nValue>(
    () => ({
      t: (key, vars) => {
        let out: string = en[key] ?? String(key);
        if (vars) {
          for (const [name, value] of Object.entries(vars)) {
            out = out.replaceAll(`{${name}}`, String(value));
          }
        }
        return out;
      },
    }),
    [],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider');
  return ctx;
}
